import React, { useEffect, useMemo, useState } from 'react';
import PageLoader from '../PageLoader';
import { axiosInstanceV2 } from '../../services/axios';
import { toast } from 'react-toastify';
import { usePayment } from '../../hooks/usePayment';
import { useDispatch } from 'react-redux';
import PricingCard from '../../pages/subscriptionPlan/components/PricingCard';
import { useNavigate, useParams } from 'react-router-dom';
import { setBillingType, setPlan } from '../../store/reducers/subscriptionSlice/subscriptionSlice';
import useSubscription from '../../hooks/useSubscription';
import { usePricingPlans } from '../../hooks/usePricingPlans';
import useSubUrl from '../../hooks/useSubUrl';

function PricingModal({ isOpen, handleClose }) {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { suburl: suburlParam } = useParams();
	const suburl = useSubUrl();
	const activeSuburl = suburlParam || suburl;
	const { openSubscriptionCheckout } = usePayment();
	const {
		subscription,
		isSubscribed,
		isSubscriptionFree,
		isSubscriptionRecurring,
		subscriptionBillingPeriod,
		subscriptionPaymentMethod,
	} = useSubscription();
	const { plans, loading } = usePricingPlans({ enabled: isOpen });
	const [billing, setBilling] = useState(subscriptionBillingPeriod ?? 'monthly');
	const [confirmModal, setConfirmModal] = useState(null);
	const [processing, setProcessing] = useState(false);

	// handle plan selection
	const handleSelectPlan = (plan, billingDetails, type) => {
		setConfirmModal({
			plan,
			billingDetails,
			type,
		});
	};

	// confirm upgrade
	const handleConfirmPlanChange = async () => {
		setProcessing(true);
		const { plan, billingDetails } = confirmModal;
		// Route to checkout whenever there is no PAID subscription to change —
		// i.e. the user is on Free, or has no subscription at all. Those users
		// have no Razorpay customer and no billing details yet, so /change-plan
		// cannot work; they must go through checkout to enter billing details and
		// pick recurring vs one-time.
		//
		// This condition used to read `!isSubscriptionFree && !isSubscribed`,
		// which is inverted for the Free case. It only appeared to work because
		// isSubscriptionFree was permanently false (it read plans.is_free, a
		// column that does not exist). Once that was corrected, Free users
		// started falling through to /change-plan and hit
		// "Razorpay customer not found."
		if (isSubscriptionFree || !isSubscribed) {
			dispatch(setPlan(plan));
			dispatch(setBillingType(billing));
			setConfirmModal(null);
			handleClose();
			// PricingModal is only reachable from within the already-authenticated
			// app, so a suburl is almost always known here — this is the
			// "existing org, currently unsubscribed/free" resubscribe path.
			navigate(activeSuburl ? `/user/${activeSuburl}/checkout` : '/checkout');
			setProcessing(false);
			return;
		}

		let result = null;

		try {
			const authToken = localStorage.getItem('authToken');
			const response = await axiosInstanceV2.post(
				'/subscription/change-plan',
				{
					razorpay_plan_id: billingDetails.razorpay_plan_id,
					billing_period: billingDetails.period,
					is_recurring: isSubscriptionRecurring,
				},
				{ headers: { Authorization: `Bearer ${authToken}` } },
			);

			result = response.data.data;

			// The backend now tells us whether a payment window is needed, instead of
			// the client inferring it from the change type.
			//
			// A RECURRING upgrade no longer opens checkout at all: Razorpay charges
			// the prorated difference against the mandate already on file, and only
			// applies the plan change if that charge succeeds. Opening a payment
			// window here would ask the customer to pay a second time.
			//
			// Checkout is still required where there is no mandate to charge — a
			// one-time subscription, or an org coming off the Free plan.
			if (result.requires_checkout) {
				try {
					await openSubscriptionCheckout({
						subscription: result.subscription,
						billingDetails: result.billing_details,
						paymentMethod: subscriptionPaymentMethod,
					});
				} catch (checkoutErr) {
					console.error('Checkout error:', checkoutErr);
					toast.error('Payment window failed to open. Please try again.');
					return;
				}
			}

			// `applies_at` is the backend's own statement of timing, so the wording
			// cannot drift from what actually happened. It also distinguishes a
			// recurring renewal from a non-recurring expiry, which the PRD requires
			// we never conflate.
			const appliedImmediately = result.applies_at === 'immediately';

			toast.success(
				result.message ??
					(appliedImmediately
						? `You're now on ${plan.name}.`
						: `Change to ${plan.name} scheduled for ${new Date(result.effective_date).toLocaleDateString()}`),
			);

			setConfirmModal(null);
			handleClose();

			// Refresh unless checkout is still in flight — an immediate upgrade has
			// already changed the org's limits, and a scheduled change needs the
			// banner to appear. The checkout path reloads from its own success
			// handler once payment completes.
			if (!result.requires_checkout) {
				window.location.reload();
			}
		} catch (error) {
			console.error('Plan change error:', error);
			const status = error?.response?.status;
			const backendMessage = error?.response?.data?.message;
			const backendCode = error?.response?.data?.code;

			if (!error?.response) {
				toast.error('Network error — check your connection and try again.');
			} else if (status === 401) {
				toast.error('Your session has expired. Please log in again.');
			} else if (backendCode === 'CHECKOUT_REQUIRED') {
				// Safety net: the org has no billing set up, so /change-plan can
				// never succeed. Send them through checkout instead of showing a
				// dead-end error.
				dispatch(setPlan(plan));
				dispatch(setBillingType(billing));
				setConfirmModal(null);
				handleClose();
				navigate(activeSuburl ? `/user/${activeSuburl}/checkout` : '/checkout');
				return;
			} else {
				toast.error(backendMessage ?? 'Failed to update subscription. Please try again.');
			}
			// CHANGED: modal stays open on failure — no setConfirmModal(null) here,
			// so the user can retry without reselecting the plan
		} finally {
			setProcessing(false); // CHANGED: this alone in finally now — no result access here
		}
	};

	const filteredPlans = useMemo(() => {
		return plans
			.filter((plan) => plan.key !== 'free')
			.filter((plan) => plan.billing?.some((billingPlan) => billingPlan.period === billing));
	}, [plans, billing]);

	useEffect(() => {
		if (!isOpen) return;
		setBilling(subscriptionBillingPeriod || 'monthly');
	}, [isOpen, subscriptionBillingPeriod]);

	if (!isOpen) return null;

	return (
		<>
			<div
				className="modal fade show d-block"
				tabIndex="-1"
				aria-modal="true"
				role="dialog"
				style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
			>
				<div className="modal-dialog modal-dialog-top modal-xl">
					<div className="modal-content">
						<div className="modal-header">
							<h5 className="modal-title">Change Subscription Plan</h5>
							<button
								type="button"
								className="btn-close"
								onClick={handleClose}
							/>
						</div>

						<div className="d-flex justify-content-center align-items-center py-4">
							<div className="billing-toggle">
								<button
									className={billing === 'monthly' ? 'active' : ''}
									onClick={() => setBilling('monthly')}
								>
									Monthly
								</button>
								<button
									className={billing === 'yearly' ? 'active' : ''}
									onClick={() => setBilling('yearly')}
								>
									Yearly
								</button>
							</div>
						</div>

						<div className="modal-body w-100">
							{loading ? (
								<PageLoader />
							) : filteredPlans.length === 0 ? (
								<div className="text-center py-5">
									<h5>No Plans Available</h5>
									<p className="text-muted mb-0">
										No {billing} subscription plans are currently available.
									</p>
								</div>
							) : (
								<div className="pricing-grid">
									{filteredPlans.map((plan) => {
										const billingDetails = plan.billing.find((item) => item.period === billing);
										return (
											<PricingCard
												key={plan._id || plan.key}
												plan={plan}
												billing={billing}
												billingDetails={billingDetails}
												currentPlanKey={subscription?.plan?.key}
												currentRazorpayPlanId={subscription?.plan?.razorpay_plan_id}
												// Per-billing-entry, so the label tracks the monthly/yearly
												// toggle. The card-level value describes only the cycle the
												// user is currently on, which made the other cycle's button
												// read wrongly — most visibly "Downgrade Plan" on an annual
												// switch that is charged immediately as an upgrade.
												changeType={billingDetails?.change_type ?? plan.change_type}
												onSelectPlan={handleSelectPlan}
											/>
										);
									})}
								</div>
							)}
						</div>
						<div className="modal-footer">
							<button
								type="button"
								className="btn-cancel"
								onClick={handleClose}
							>
								Close
							</button>
						</div>
					</div>
				</div>
			</div>

			{/* confirmation modal */}
			{confirmModal && (
				<div
					className="modal fade show d-block"
					style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }}
				>
					<div className="modal-dialog modal-dialog-centered">
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title">
									{isSubscriptionFree
										? 'Subscribe to Plan'
										: confirmModal.type === 'upgrade'
											? 'Confirm Upgrade'
											: 'Confirm Downgrade'}
								</h5>
								<button
									type="button"
									className="btn-close"
									onClick={() => setConfirmModal(null)}
								/>
							</div>

							<div className="modal-body">
								<div className="modal-body">
									{isSubscriptionFree ? (
										<p>
											You're subscribing to the
											<strong> {confirmModal.plan.name}</strong> plan. Click Continue to provide
											your billing details before completing payment.
										</p>
									) : confirmModal.type === 'upgrade' ? (
										<p>
											After successful payment, your
											<strong> {confirmModal.plan.name}</strong> plan will become active
											immediately.
										</p>
									) : (
										<p>
											You're upgrading to <strong>{confirmModal.plan.name}</strong> (
											{confirmModal.billingDetails.period}) for{' '}
											<strong>₹{confirmModal.billingDetails.amount_in_rupees.toFixed(2)}</strong>.
										</p>
									)}
								</div>
							</div>

							<div className="modal-footer">
								<button
									type="button"
									className="btn-cancel"
									onClick={() => setConfirmModal(null)}
								>
									Cancel
								</button>
								<button
									disabled={processing}
									type="button"
									className="btn-primary-modal"
									onClick={handleConfirmPlanChange}
								>
									{isSubscriptionFree
										? 'Continue'
										: confirmModal.type === 'upgrade'
											? 'Proceed to Payment'
											: 'Confirm Downgrade'}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	);
}

export default PricingModal;
